import { PayoffRequestStatus } from '@upstart/harmony/lib/legos/lossExpressTypes';
import moment from 'moment';
import { TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import { fmtPrice } from '@upstart/harmony/lib/formatter';

export enum TradePayoffState {
  DEFAULT = 'default',
  NOT_FOUND = 'notFound',
  PENDING = 'pending',
  PENDING_UPDATE = 'pendingUpdate',
  VALID = 'valid',
  EXPIRED = 'expired',
}

export interface PayoffStatusProperties {
  tooltip: (tradeIn?: Partial<TradeIn>) => string;
  status: (tradeIn?: Partial<TradeIn>) => string;
  inputDisabled: boolean;
}

const PAYOFF_LOOKUP_IN_PROGRESS_TEXT =
  'We are searching for the most accurate trade payoff. This may take up to 24 hours. While you wait, you may continue the deal by estimating the trade payoff manually. Note that any estimated amount will be replaced by the most accurate payoff amount we find, but will not change in progress lending applications or completed deals.';

const fmtExpirationDate = (date?: string) =>
  date ? moment(date).format('l') : '';
const fmtPerDiem = (perDiem?: number) => (perDiem ? fmtPrice(perDiem) : '');

export const TradeInStatusMap = {
  default: {
    status: () => 'Start lookup in iPad',
    tooltip: () => '',
    inputDisabled: false,
  } as PayoffStatusProperties,
  valid: {
    status: (tradeIn?: Partial<TradeIn>) =>
      `Valid through ${fmtExpirationDate(tradeIn?.payoffExpiredDate)}`,
    tooltip: (tradeIn?: Partial<TradeIn>) =>
      'This is the most accurate Trade Payoff and cannot be edited. This quote is valid through ' +
      `${fmtExpirationDate(tradeIn?.payoffExpiredDate)}.`,
    inputDisabled: true,
  } as PayoffStatusProperties,
  expired: {
    status: (tradeIn?: Partial<TradeIn>) =>
      `Expired ${fmtExpirationDate(
        tradeIn?.payoffExpiredDate,
      )}. Per-diem of ${fmtPerDiem(tradeIn?.perDiem)}`,
    tooltip: (tradeIn?: Partial<TradeIn>) =>
      `This payoff quote is expired. The per diem is ${fmtPerDiem(
        tradeIn?.perDiem,
      )}. We recommend you refresh the quote to find the most accurate 
      trade payoff amount.`,
    inputDisabled: true,
  } as PayoffStatusProperties,
  pendingUpdate: {
    status: (tradeIn?: Partial<TradeIn>) =>
      `Expired ${fmtExpirationDate(
        tradeIn?.payoffExpiredDate,
      )} (New lookup in progress...)`,
    tooltip: () =>
      'This payoff quote is expired. We are looking up the most accurate trade payoff ' +
      'amount.',
    inputDisabled: true,
  } as PayoffStatusProperties,
  pending: {
    status: () => 'Lookup in progress...',
    tooltip: () => PAYOFF_LOOKUP_IN_PROGRESS_TEXT,
    inputDisabled: false,
  } as PayoffStatusProperties,
  notFound: {
    status: () => 'Payoff amount not found',
    tooltip: () =>
      `We could not find the trade payoff for this customer's vehicle. Continue by entering ` +
      'the trade payoff manually.',
    inputDisabled: false,
  } as PayoffStatusProperties,
};

export const getTradeInPayoffState = (
  dealTradeIn: TradeIn,
): TradePayoffState => {
  if (!dealTradeIn?.payoffRequestStatus) {
    return TradePayoffState.DEFAULT;
  }

  const { payoffRequestStatus } = dealTradeIn;
  if (payoffRequestStatus === PayoffRequestStatus.Error) {
    return TradePayoffState.NOT_FOUND;
  }

  if (payoffRequestStatus === PayoffRequestStatus.Pending) {
    // If we have an expiry date set, then this is a refresh lookup. Otherwise
    // we're performing the initial lookup of the payoff value
    return dealTradeIn.payoffExpiredDate
      ? TradePayoffState.PENDING_UPDATE
      : TradePayoffState.PENDING;
  }

  if (payoffRequestStatus === PayoffRequestStatus.Success) {
    const now = moment();
    const expiredDate = moment(dealTradeIn.payoffExpiredDate);
    if (now.isSameOrBefore(expiredDate, 'day')) {
      return TradePayoffState.VALID;
    }

    // if expired and an estimated amount has been entered, return to default state (allows for new lookup to be performed)
    if (dealTradeIn.payoffEstimated) {
      return TradePayoffState.DEFAULT;
    }
    return TradePayoffState.EXPIRED;
  }

  return TradePayoffState.DEFAULT;
};

export const TradeInStatusMapDeskingV2 = {
  ...TradeInStatusMap,
  default: {
    ...TradeInStatusMap.default,
    status: () => 'Start payoff lookup',
    tooltip: () =>
      'Click the start payoff link to provide customer trade info and get the most accurate trade payoff amount.',
  },
  pendingUpdate: {
    ...TradeInStatusMap.pendingUpdate,
    status: (tradeIn?: Partial<TradeIn>) =>
      tradeIn?.payoffEstimated
        ? 'Payoff lookup in progress...'
        : `Expired ${fmtExpirationDate(tradeIn?.payoffExpiredDate)}`,
    tooltip: () =>
      `This payoff quote is expired. ${PAYOFF_LOOKUP_IN_PROGRESS_TEXT}`,
    inputDisabled: false,
  },
  expired: {
    ...TradeInStatusMap.expired,
    inputDisabled: false,
    tooltip: () =>
      'This payoff quote is expired. We recommend you click the link to refresh payoff lookup and get the most accurate trade payoff, or continue by estimating the trade payoff manually.',
  },
  notFound: {
    ...TradeInStatusMap.notFound,
    tooltip: () =>
      "We could not find the trade payoff for this customer's vehicle. Continue by entering the trade payoff manually, or click the link to try again by starting a new trade payoff lookup.",
  },
};

export const INPUT_DISABLED_BY_LOSS_EXPRESS_TOOLTIP =
  'This is the most accurate Trade Payoff and cannot be edited.';

export const NO_PAYOFF_ADDED =
  'There is no trade-in on this deal. We recommend you add a trade in iPad or Online so that you can get the most accurate trade payoff, or continue by estimating the trade payoff manually.';
